import { useEffect, useState } from 'react'
import { Bot, History, Plus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { AssistantHome } from '@/src/components/assistant/AssistantHome'
import { AssistantThreadView } from '@/src/components/assistant/AssistantThread'
import { useAssistantSession } from '@/src/components/assistant/AssistantSession'
import { HistoryDrawer } from '@/src/components/assistant/ConversationHistory'
import { apiClient } from '@/lib/api/client'
import { useAsyncData } from '@/src/hooks/useAsyncData'
import { getHelmSessionUser } from '@/src/lib/auth'

/** The name to greet someone by: the first word of theirs, never an email address. */
function firstNameOf(name: string | null | undefined): string | null {
  const first = name?.trim().split(/\s+/)[0]
  return first && !first.includes('@') ? first : null
}

/**
 * Full-page assistant.
 *
 * With nothing open it is a home: a question and one large box in the middle,
 * and the last conversations beneath. With a conversation open it is that
 * conversation, filling the page, with the box at the bottom. Past
 * conversations are in a drawer from the header rather than a column that is
 * always there.
 *
 * It shows the session's conversation, the same one the widget shows: opening
 * this page during a call shows that call rather than starting another.
 */
export function AssistantPage() {
  const session = useAssistantSession()
  const { assistant, threads, threadId, selectThread, renameThread, activate } = session
  const [historyOpen, setHistoryOpen] = useState(false)
  const user = useAsyncData(() => getHelmSessionUser().catch(() => null), [])
  const profile = useAsyncData(() => apiClient.getProfile().catch(() => null), [])
  const isAdmin = Boolean(profile.data?.is_admin || profile.data?.role === 'admin')

  useEffect(() => activate(), [activate])

  const open = (id: string | null) => {
    selectThread(id)
    setHistoryOpen(false)
  }
  const title = threads.find((thread) => thread.id === threadId)?.title ?? null

  return (
    // The shell's header is 4rem and <main> pads 0.75rem (1.5rem from md) each
    // side: the page takes exactly what is left, so the box stays on screen.
    <div className="flex h-[calc(100dvh-5.5rem)] min-h-0 flex-col md:h-[calc(100dvh-7rem)]">
      <header className="flex shrink-0 items-center gap-2 pb-2">
        {!assistant.blank && (
          <>
            <Bot aria-hidden className="size-4 shrink-0 text-muted-foreground" />
            <h1 className="min-w-0 truncate text-sm font-medium">{title ?? 'Beacon'}</h1>
          </>
        )}
        <div className="ml-auto flex shrink-0 items-center gap-1.5">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setHistoryOpen(true)}
            aria-haspopup="dialog"
          >
            <History />
            {/* A word beside the title on a phone costs the title its room. */}
            <span className={assistant.blank ? undefined : 'sr-only sm:not-sr-only'}>History</span>
          </Button>
          {!assistant.blank && (
            <Button variant="outline" size="sm" onClick={() => open(null)}>
              <Plus />
              New chat
            </Button>
          )}
        </div>
      </header>

      {assistant.blank ? (
        <AssistantHome
          assistant={assistant}
          mentionItems={session.mentionItems}
          firstName={firstNameOf(user.data?.name)}
          isAdmin={isAdmin}
          threads={threads}
          onOpenThread={open}
          onViewAll={() => setHistoryOpen(true)}
        />
      ) : (
        // Eases in from the home, so the first message reads as moving up into
        // its conversation rather than the page being swapped.
        <div className="min-h-0 flex-1 animate-in duration-300 fade-in-0 slide-in-from-bottom-1">
          <AssistantThreadView
            assistant={assistant}
            mentionItems={session.mentionItems}
            layout="page"
          />
        </div>
      )}

      <HistoryDrawer
        open={historyOpen}
        onOpenChange={setHistoryOpen}
        threads={threads}
        activeId={threadId}
        onSelect={open}
        onNew={() => open(null)}
        onRename={renameThread}
      />
    </div>
  )
}

export default AssistantPage
